import { call, put, takeEvery } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import apiService from '../../api/service/apiService';
import type { ApiResponse } from '../../api/service/apiService';
import { PROMOFLOW_BASE_API_URL } from '../../api/endPoints';
import {
  fetchIntegrationsRequest,
  fetchIntegrationsSuccess,
  fetchIntegrationsFailure,
  activateIntegrationRequest,
  activateIntegrationSuccess,
  activateIntegrationFailure,
  deactivateIntegrationRequest,
  deactivateIntegrationSuccess,
  deactivateIntegrationFailure,
} from '../slices/integrationsSlice';
import type { Integration } from '../slices/integrationsSlice';
import { openOAuthPopup } from '../../utils/oauthHelper';

/**
 * Fetch all integrations for a restaurant
 */
function* fetchIntegrationsSaga(
  action: PayloadAction<{ restaurantId: string }>
): Generator<any, void, ApiResponse<Integration[]>> {
  try {
    const { restaurantId } = action.payload;
    const endpoint = `${PROMOFLOW_BASE_API_URL.triggerUrl}/integrations/${restaurantId}`;

    const response: ApiResponse<Integration[]> = yield call(apiService.get, {
      endpoint,
    });

    yield put(fetchIntegrationsSuccess(response.data || []));
  } catch (error: any) {
    yield put(
      fetchIntegrationsFailure(error.message || 'Failed to fetch integrations')
    );
  }
}

/**
 * Activate integration via OAuth flow
 * Opens popup window for OAuth authentication
 */
function* activateIntegrationSaga(
  action: PayloadAction<{ platform: string; restaurantId: string }>
): Generator<any, void, any> {
  try {
    const { platform, restaurantId } = action.payload;

    // Build OAuth login URL
    const loginUrl = `${PROMOFLOW_BASE_API_URL.triggerUrl}/${platform}/login/${restaurantId}`;

    // Open OAuth popup and wait for completion
    yield call(openOAuthPopup, {
      url: loginUrl,
      platform,
      onSuccess: () => {
        // Popup closed successfully - will trigger success action
        console.log(`${platform} OAuth flow completed`);
      },
      onError: (error: string) => {
        console.error(`${platform} OAuth error:`, error);
      },
    });

    yield put(activateIntegrationSuccess());

    // Refetch integrations to get updated list
    yield put(fetchIntegrationsRequest({ restaurantId }));
  } catch (error: any) {
    yield put(
      activateIntegrationFailure(
        error.message || `Failed to activate ${action.payload.platform}`
      )
    );
  }
}

/**
 * Deactivate/delete an integration
 */
function* deactivateIntegrationSaga(
  action: PayloadAction<{ restaurantId: string; integrationId: number }>
): Generator<any, void, any> {
  try {
    const { restaurantId, integrationId } = action.payload;
    const endpoint = `${PROMOFLOW_BASE_API_URL.triggerUrl}/integrations/${restaurantId}/${integrationId}`;

    yield call(apiService.delete, { endpoint });
    yield put(deactivateIntegrationSuccess());

    // Refetch integrations to update the list
    yield put(fetchIntegrationsRequest({ restaurantId }));
  } catch (error: any) {
    yield put(
      deactivateIntegrationFailure(
        error.message || 'Failed to deactivate integration'
      )
    );
  }
}

/**
 * Root saga for integrations
 */
export function* integrationsSaga(): Generator<any, void, any> {
  yield takeEvery(fetchIntegrationsRequest.type, fetchIntegrationsSaga);
  yield takeEvery(activateIntegrationRequest.type, activateIntegrationSaga);
  yield takeEvery(deactivateIntegrationRequest.type, deactivateIntegrationSaga);
}

export default integrationsSaga;
